import React, { createContext, useMemo, useState } from "react";

type LayoutContextType = {
    isLanguageSelectorOpen: boolean;
    setLanguageSelectorOpen?: (isOpen: boolean) => void;
};

/**
 * @description Context to manage the layout state, specifically the language selector.
 *
 * @author HBM
 */
export const LayoutContext = createContext<LayoutContextType>({
    isLanguageSelectorOpen: false
});

type Props = {
    children: React.ReactNode;
};

/**
 * @description Provider component to wrap the application and provide layout context.
 * @param {Props} props - The children components to be wrapped by the provider.
 * @returns {JSX.Element} The provider component with layout context.
 */
export function LayoutProvider({ children }: Props): JSX.Element {
    const [isLanguageSelectorOpen, setLanguageSelectorOpen] = useState(false);
    const value = useMemo<LayoutContextType>(
        () => ({ isLanguageSelectorOpen, setLanguageSelectorOpen }),
        [isLanguageSelectorOpen]
    );

    return (
        <LayoutContext.Provider value={value}>
            {children}
        </LayoutContext.Provider>
    );
}
